import type {
  AppThemeDoc,
  ThemeActionButtons,
  ThemeStatusBadges,
  ThemeProgressBar,
  ThemeChartColors,
  ThemeIconAccents,
  ThemeStateStyles,
} from '../types';

/** Default action button styles for transaction reflections */
export const DEFAULT_ACTION_BUTTONS: ThemeActionButtons = {
  needed: {
    bg: 'bg-foliage',
    bgHover: 'hover:opacity-90',
    text: 'text-white',
    textHover: 'hover:text-white',
    shadow: 'shadow-foliage/30',
  },
  wanted: {
    bg: 'bg-sky',
    bgHover: 'hover:opacity-90',
    text: 'text-white',
    textHover: 'hover:text-white',
    shadow: 'shadow-sky/30',
  },
  regret: {
    bg: 'bg-clay',
    bgHover: 'hover:opacity-90',
    text: 'text-white',
    textHover: 'hover:text-white',
    shadow: 'shadow-clay/30',
  },
};

/** Default status badge styles for goals and connections */
export const DEFAULT_STATUS_BADGES: ThemeStatusBadges = {
  active: { bg: 'bg-growth/10', text: 'text-growth' },
  completed: { bg: 'bg-sage/20', text: 'text-sage' },
  warning: { bg: 'bg-clay/20', text: 'text-clay' },
  error: { bg: 'bg-wildfire/20', text: 'text-wildfire' },
};

/** Default progress bar styles for goals */
export const DEFAULT_PROGRESS_BAR: ThemeProgressBar = {
  track: 'bg-sand',
  fillLow: 'bg-growth',
  fillMedium: 'bg-foliage',
  fillComplete: 'bg-sage',
};

/** Default chart colors for alignment chart (full class names) */
export const DEFAULT_CHART_COLORS: ThemeChartColors = {
  series1: 'bg-foliage',
  series2: 'bg-sky',
  series3: 'bg-clay',
};

/** Default icon accent colors */
export const DEFAULT_ICON_ACCENTS: ThemeIconAccents = {
  primary: 'foliage',
  success: 'sage',
  warning: 'clay',
  error: 'wildfire',
  info: 'sky',
};

/** Default state styles for semantic card states */
export const DEFAULT_STATE_STYLES: ThemeStateStyles = {
  positive: {
    cardBg: 'bg-sage/10',
    cardBgHover: 'hover:bg-sage/15',
    cardBorder: 'border-sage/20',
    cardShadow: 'shadow-sage/10',
    decorativeBg: 'bg-sage/20',
    text: 'text-growth',
  },
  caution: {
    cardBg: 'bg-clay/10',
    cardBgHover: 'hover:bg-clay/15',
    cardBorder: 'border-clay/20',
    cardShadow: 'shadow-clay/10',
    decorativeBg: 'bg-clay/20',
    text: 'text-growth',
  },
  negative: {
    cardBg: 'bg-wildfire/5',
    cardBgHover: 'hover:bg-wildfire/10',
    cardBorder: 'border-wildfire/20',
    cardShadow: 'shadow-wildfire/10',
    decorativeBg: 'bg-wildfire/10',
    text: 'text-growth',
  },
  neutral: {
    cardBg: 'bg-mist/10',
    cardBgHover: 'hover:bg-mist/15',
    cardBorder: 'border-mist/20',
    cardShadow: 'shadow-mist/10',
    decorativeBg: 'bg-mist/20',
    text: 'text-growth/70',
  },
};

/**
 * Merge an AppThemeDoc with default extension values.
 * Returns a complete theme with all optional fields populated.
 * Does deep merge for nested objects to ensure new fields get defaults.
 */
export function getCompleteTheme(theme: AppThemeDoc): Required<AppThemeDoc> {
  // Deep merge action buttons to ensure new fields (like textHover) get defaults
  const mergedActionButtons: ThemeActionButtons = theme.actionButtons
    ? {
        needed: { ...DEFAULT_ACTION_BUTTONS.needed, ...theme.actionButtons.needed },
        wanted: { ...DEFAULT_ACTION_BUTTONS.wanted, ...theme.actionButtons.wanted },
        regret: { ...DEFAULT_ACTION_BUTTONS.regret, ...theme.actionButtons.regret },
      }
    : DEFAULT_ACTION_BUTTONS;

  // Deep merge state styles
  const mergedStateStyles: ThemeStateStyles = theme.stateStyles
    ? {
        positive: { ...DEFAULT_STATE_STYLES.positive, ...theme.stateStyles.positive },
        caution: { ...DEFAULT_STATE_STYLES.caution, ...theme.stateStyles.caution },
        negative: { ...DEFAULT_STATE_STYLES.negative, ...theme.stateStyles.negative },
        neutral: { ...DEFAULT_STATE_STYLES.neutral, ...theme.stateStyles.neutral },
      }
    : DEFAULT_STATE_STYLES;

  // Deep merge status badges
  const mergedStatusBadges: ThemeStatusBadges = theme.statusBadges
    ? {
        active: { ...DEFAULT_STATUS_BADGES.active, ...theme.statusBadges.active },
        completed: { ...DEFAULT_STATUS_BADGES.completed, ...theme.statusBadges.completed },
        warning: { ...DEFAULT_STATUS_BADGES.warning, ...theme.statusBadges.warning },
        error: { ...DEFAULT_STATUS_BADGES.error, ...theme.statusBadges.error },
      }
    : DEFAULT_STATUS_BADGES;

  return {
    ...theme,
    componentType: theme.componentType ?? 'exercises',
    actionButtons: mergedActionButtons,
    statusBadges: mergedStatusBadges,
    progressBar: theme.progressBar ? { ...DEFAULT_PROGRESS_BAR, ...theme.progressBar } : DEFAULT_PROGRESS_BAR,
    chartColors: theme.chartColors ? { ...DEFAULT_CHART_COLORS, ...theme.chartColors } : DEFAULT_CHART_COLORS,
    iconAccents: theme.iconAccents ? { ...DEFAULT_ICON_ACCENTS, ...theme.iconAccents } : DEFAULT_ICON_ACCENTS,
    stateStyles: mergedStateStyles,
  };
}
